import { PE_LOGO_PUBLIC_URL } from '../constants/brandAssets';
import { formatHalloGreeting } from './mailGreeting';
import { clarifyOnlineFormat, RECORDING_LIVE_ONLY_COPY } from './onlineEventWording';

const EVENT_HOST = 'https://pe-one.friseur-coaching.de';

function escapeHtml(value: string): string {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function sanitizeAccessUrl(raw?: string | null): string {
  const value = String(raw || '').trim();
  if (!/^https:\/\//i.test(value) || value.length > 2000) return '';
  return value;
}

export type OnlineEventConfirmInput = {
  firstName?: string | null;
  title: string;
  slug: string;
  dateLabel?: string;
  time?: string;
  format?: string;
  accessUrl?: string | null;
  accessUrlEvening?: string | null;
  willBeRecorded?: boolean | null;
};

export type OnlineEventConfirmMail = {
  subject: string;
  html: string;
  text: string;
};

export function buildOnlineEventConfirmMail(input: OnlineEventConfirmInput): OnlineEventConfirmMail {
  const greeting = formatHalloGreeting(input.firstName);
  const title = String(input.title || 'Online-Event').trim();
  const dateLabel = String(input.dateLabel || '').trim();
  const time = String(input.time || '').trim();
  const format = clarifyOnlineFormat(input.format);
  const eventUrl = `${EVENT_HOST}/event/${String(input.slug || '').trim().toLowerCase()}`;
  const accessUrl = sanitizeAccessUrl(input.accessUrl);
  const accessUrlEvening = sanitizeAccessUrl(input.accessUrlEvening);
  const recordingNotice = input.willBeRecorded === true ? RECORDING_LIVE_ONLY_COPY : '';
  const subject = `Dein Platz ist reserviert: ${title}`;

  const whenParts = [dateLabel, time].filter(Boolean);
  const whenLine = whenParts.length ? whenParts.join(' · ') : '';

  const accessText = accessUrl
    ? [
        'Dein Zugangslink (bitte speichern — er gilt auch, wenn das Event erst in ein paar Wochen ist):',
        accessUrl,
        accessUrlEvening && accessUrlEvening !== accessUrl ? `Abend-Session: ${accessUrlEvening}` : '',
        '',
        'Zusätzlich erinnern wir dich 7 Tage und 12 Stunden vor Beginn.',
      ]
        .filter(Boolean)
        .join('\n')
    : 'Dein Platz ist sicher. Den Zugangslink senden wir dir, sobald er hinterlegt ist — spätestens mit der Erinnerung 7 Tage vor Beginn. Bis dahin musst du nichts tun.';

  const accessHtml = accessUrl
    ? [
        `<p style="margin:0 0 16px 0;font-size:16px;line-height:1.5;"><strong>Dein Zugangslink</strong> — bitte speichern, er gilt auch, wenn das Event erst in ein paar Wochen ist:</p>`,
        `<p style="margin:0 0 16px 0;"><a href="${escapeHtml(accessUrl)}" style="display:inline-block;background:#DCB68A;color:#0A0A0A;text-decoration:none;font-weight:800;letter-spacing:0.08em;text-transform:uppercase;font-size:12px;padding:12px 18px;border-radius:10px;">Jetzt zum Event</a></p>`,
        `<p style="margin:0 0 16px 0;font-size:13px;line-height:1.5;color:#666;word-break:break-all;">${escapeHtml(accessUrl)}</p>`,
        accessUrlEvening && accessUrlEvening !== accessUrl
          ? `<p style="margin:0 0 16px 0;font-size:14px;line-height:1.5;">Abend-Session: <a href="${escapeHtml(accessUrlEvening)}" style="color:#DCB68A;">${escapeHtml(accessUrlEvening)}</a></p>`
          : '',
        `<p style="margin:0 0 16px 0;font-size:16px;line-height:1.5;">Zusätzlich erinnern wir dich 7 Tage und 12 Stunden vor Beginn.</p>`,
      ].join('')
    : `<p style="margin:0 0 16px 0;font-size:16px;line-height:1.5;">Dein Platz ist sicher. Den Zugangslink senden wir dir, sobald er hinterlegt ist — spätestens mit der Erinnerung 7 Tage vor Beginn. Bis dahin musst du nichts tun.</p>`;

  const text = `${greeting}

dein Platz für „${title}“ ist fest reserviert. Das Event findet online statt — du musst nirgendwohin fahren.

${whenLine ? `Termin: ${whenLine}\n` : ''}Format: ${format}

${accessText}

${recordingNotice ? `Wichtig: ${recordingNotice}\n` : ''}Event-Seite: ${eventUrl}

Herzliche Grüße
Dein PreisExpert-Team
`;

  const html = [
    `<div style="text-align:center;margin:0 0 24px 0;padding-bottom:16px;border-bottom:1px solid #333;">`,
    `<img src="${PE_LOGO_PUBLIC_URL}" alt="PreisExpert" style="max-height:72px;width:auto;max-width:240px;display:inline-block;" />`,
    `</div>`,
    `<p style="margin:0 0 16px 0;font-size:16px;line-height:1.5;">${escapeHtml(greeting)}</p>`,
    `<p style="margin:0 0 16px 0;font-size:16px;line-height:1.5;">dein Platz für <strong>${escapeHtml(title)}</strong> ist fest reserviert. Das Event findet <strong>online</strong> statt — du musst nirgendwohin fahren.</p>`,
    whenLine
      ? `<p style="margin:0 0 16px 0;font-size:16px;line-height:1.5;"><strong>Termin:</strong> ${escapeHtml(whenLine)}<br/><strong>Format:</strong> ${escapeHtml(format)}</p>`
      : `<p style="margin:0 0 16px 0;font-size:16px;line-height:1.5;"><strong>Format:</strong> ${escapeHtml(format)}</p>`,
    accessHtml,
    recordingNotice
      ? `<p style="margin:0 0 16px 0;font-size:16px;line-height:1.5;padding:12px 14px;border:1px solid #b45309;border-radius:10px;background:#1c1917;"><strong>Bitte beachten:</strong> ${escapeHtml(recordingNotice)}</p>`
      : '',
    `<p style="margin:0 0 16px 0;font-size:16px;line-height:1.5;"><a href="${escapeHtml(eventUrl)}" style="color:#DCB68A;">Event-Seite öffnen</a></p>`,
    `<p style="margin:0;font-size:16px;line-height:1.5;">Herzliche Grüße<br/>Dein PreisExpert-Team</p>`,
  ].join('');

  return { subject, html, text };
}
